import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { MusicIcon, ConfirmationModal, useConfirmation, RejectModal } from '../../components/shared';
import { useTranslation } from 'react-i18next';

const AllReleases: React.FC = () => {
  const { releases, updateReleaseStatus, getHybridCoverArtUrl } = useData();
  const { t } = useTranslation();
  const { confirmationState, showConfirmation, handleCancel } = useConfirmation();
  const [coverUrls, setCoverUrls] = useState<Record<string, string>>({});
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest'); // Sıralama durumu
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(25); // Her sayfada varsayılan 25 release göster
  const [rejectModal, setRejectModal] = useState<{
    isOpen: boolean;
    releaseId: string;
    releaseTitle: string;
  }>({
    isOpen: false,
    releaseId: '',
    releaseTitle: ''
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
      case 'approved':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'pending':
      case 'submitted':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'draft':
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
      case 'rejected':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'published':
        return t('status.published');
      case 'approved':
        return t('status.approved');
      case 'pending':
        return t('status.pending');
      case 'submitted':
        return t('status.submitted');
      case 'draft':
        return t('status.draft');
      case 'rejected':
        return t('status.rejected');
      default:
        return status;
    }
  };

  const handleApprove = async (releaseId: string, releaseTitle: string) => {
    const confirmed = await showConfirmation({
      title: t('admin.approveConfirmTitle'),
      message: t('admin.approveConfirmMessage', { title: releaseTitle }),
      confirmText: t('admin.approveAction'),
      type: 'info'
    });

    if (confirmed) {
      updateReleaseStatus(releaseId, 'approved');
    }
  };

  const handleReject = (releaseId: string, releaseTitle: string) => {
    setRejectModal({
      isOpen: true,
      releaseId,
      releaseTitle
    });
  };

  const handleRejectConfirm = async (reason: string) => {
    const success = await updateReleaseStatus(rejectModal.releaseId, 'rejected', reason);
    if (success) {
      setRejectModal({
        isOpen: false,
        releaseId: '',
        releaseTitle: ''
      });
    }
  };

  const handleRejectCancel = () => {
    setRejectModal({
      isOpen: false,
      releaseId: '',
      releaseTitle: ''
    });
  };

  const handleRemoveFromPublication = async (releaseId: string, releaseTitle: string) => {
    const confirmed = await showConfirmation({
      title: t('admin.removeConfirmTitle'),
      message: t('admin.removeConfirmMessage', { title: releaseTitle }),
      confirmText: t('admin.removeFromPublication'),
      type: 'warning'
    });

    if (confirmed) {
      updateReleaseStatus(releaseId, 'draft');
    }
  };

  const filteredReleases = releases
    .filter(release => {
      if (statusFilter === 'all') return true;
      return release.status === statusFilter;
    })
    .sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();

      if (sortOrder === 'newest') {
        return dateB - dateA; // En yeni başta
      } else {
        return dateA - dateB; // En eski başta
      }
    });

  // Pagination hesaplamaları
  const totalPages = Math.ceil(filteredReleases.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedReleases = filteredReleases.slice(startIndex, endIndex);

  // Load cover art URLs only for current page
  useEffect(() => {
    const loadCoverUrls = async () => {
      const urls: Record<string, string> = {};

      await Promise.all(
        paginatedReleases.map(async (release) => {
          try {
            const coverUrl = await getHybridCoverArtUrl(release.id);
            if (coverUrl) {
              urls[release.id] = coverUrl;
            }
          } catch (error) {
            console.warn(`Cover URL yüklenemedi (${release.id}):`, error);
          }
        })
      );

      setCoverUrls(urls);
    };

    if (paginatedReleases.length > 0) {
      loadCoverUrls();
    }
  }, [paginatedReleases, getHybridCoverArtUrl]);

  // Filter değiştiğinde sayfayı 1'e resetle
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-white">{t('admin.allMusic')}</h1>
        <p className="mt-1 text-sm text-white/70">
          {t('admin.manageAllMusic')}
        </p>
      </div>

      {/* Filter Section */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === 'all'
                ? 'bg-primary-600 text-white'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            {t('admin.allReleases')}
          </button>
          <button
            onClick={() => setStatusFilter('published')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === 'published'
                ? 'bg-green-600 text-white'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            {t('admin.publishedFilter')}
          </button>
          <button
            onClick={() => setStatusFilter('approved')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === 'approved'
                ? 'bg-blue-600 text-white'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            {t('admin.approvedFilter')}
          </button>
          <button
            onClick={() => setStatusFilter('pending')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === 'pending'
                ? 'bg-yellow-600 text-white'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            {t('admin.pendingFilter')}
          </button>
          <button
            onClick={() => setStatusFilter('draft')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === 'draft'
                ? 'bg-gray-600 text-white'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            {t('admin.draftFilter')}
          </button>
          <button
            onClick={() => setStatusFilter('rejected')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === 'rejected'
                ? 'bg-red-600 text-white'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            {t('admin.rejectedFilter')}
          </button>
        </div>
      </div>

      {/* Sort & Items Per Page Filter */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between gap-4">
        {/* Total Count Info */}
        <div className="flex items-center bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl px-4 py-2">
          <span className="text-sm text-white/70">Toplam: </span>
          <span className="ml-2 text-lg font-bold text-orange-400">{filteredReleases.length}</span>
          <span className="ml-1 text-sm text-white/70">kayıt</span>
        </div>

        <div className="flex flex-wrap gap-3">
          {/* Items Per Page Selector */}
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-2">
            <span className="text-sm text-white/70 ml-2">Göster:</span>
            {[25, 50, 100].map((count) => (
              <button
                key={count}
                onClick={() => { setItemsPerPage(count); setCurrentPage(1); }}
                className={`px-3 py-1 text-sm font-medium rounded-lg transition-all duration-200 ${
                  itemsPerPage === count
                    ? 'bg-orange-500 text-white shadow-lg'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                {count}
              </button>
            ))}
          </div>

          {/* Sort Order */}
          <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-2">
            <span className="text-sm text-white/70 ml-2">Sıralama:</span>
            <button
              onClick={() => setSortOrder('newest')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                sortOrder === 'newest'
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              En Yeni
            </button>
            <button
              onClick={() => setSortOrder('oldest')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                sortOrder === 'oldest'
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              En Eski
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="glass-card">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-white/20">
                <th className="px-6 py-4 text-left text-xs font-medium text-white/80 uppercase tracking-wider">
                  {t('admin.release')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-white/80 uppercase tracking-wider">
                  {t('admin.artist')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-white/80 uppercase tracking-wider">
                  {t('admin.type')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-white/80 uppercase tracking-wider">
                  {t('tracks.status')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-white/80 uppercase tracking-wider">
                  {t('admin.date')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-white/80 uppercase tracking-wider">
                  {t('tracks.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {paginatedReleases.map((release) => (
                <tr key={release.id} className="hover:bg-white/5 transition-colors duration-200">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {coverUrls[release.id] ? (
                        <img
                          className="h-12 w-12 rounded-xl object-cover border border-white/20"
                          src={coverUrls[release.id]}
                          alt=""
                          onError={(e) => {
                            // Fallback to placeholder if CloudFront URL fails
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling?.setAttribute('style', 'display: flex');
                          }}
                        />
                      ) : null}
                      {!coverUrls[release.id] && (
                        <div className="h-12 w-12 flex items-center justify-center border border-white/20 rounded-xl">
                          <MusicIcon size="sm" />
                        </div>
                      )}
                      <div className="ml-4">
                        <div className="text-sm font-medium text-white">{release.title}</div>
                        <div className="text-sm text-white/60">{release.tracks.length} {t('admin.tracks')}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-white">{release.artist_display_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-white">{release.type}</div>
                    <div className="text-sm text-white/60">{release.genre}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(release.status)}`}>
                      {getStatusText(release.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white/70">
                    {new Date(release.release_date).toLocaleDateString('tr-TR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-3">
                      {/* Her zaman view butonu */}
                      <Link
                        to={`/admin/tracks/view/${release.id}`}
                        className="text-blue-300 hover:text-blue-200 transition-colors duration-200"
                      >
                        {t('admin.viewAction')}
                      </Link>
                      
                      {/* Her zaman edit butonu */}
                      <Link
                        to={`/admin/tracks/edit/${release.id}`}
                        className="text-purple-300 hover:text-purple-200 transition-colors duration-200"
                      >
                        {t('admin.editAction')}
                      </Link>
                      
                      {/* Status'a göre özel butonlar */}
                      {(release.status === 'pending' || release.status === 'submitted') && (
                        <>
                          <button
                            onClick={() => handleApprove(release.id, release.title)}
                            className="text-green-300 hover:text-green-200 transition-colors duration-200"
                          >
                            {t('admin.approveAction')}
                          </button>
                          <button
                            onClick={() => handleReject(release.id, release.title)}
                            className="text-red-300 hover:text-red-200 transition-colors duration-200"
                          >
                            {t('admin.rejectAction')}
                          </button>
                        </>
                      )}
                      
                      {(release.status === 'published' || release.status === 'approved') && (
                        <button
                          onClick={() => handleRemoveFromPublication(release.id, release.title)}
                          className="text-orange-300 hover:text-orange-200 transition-colors duration-200"
                        >
                          {t('admin.removeFromPublication')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between">
            <div className="text-sm text-white/70">
              {t('showing')} {startIndex + 1}-{Math.min(endIndex, filteredReleases.length)} {t('of')} {filteredReleases.length}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  currentPage === 1
                    ? 'bg-white/5 text-white/30 cursor-not-allowed'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {t('previous')}
              </button>

              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  // Akıllı sayfa numarası gösterimi
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNum
                          ? 'bg-primary-600 text-white'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  currentPage === totalPages
                    ? 'bg-white/5 text-white/30 cursor-not-allowed'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {t('next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        type={confirmationState.type}
        onConfirm={confirmationState.onConfirm}
        onCancel={handleCancel}
      />

      {/* Reject Modal */}
      <RejectModal
        isOpen={rejectModal.isOpen}
        releaseTitle={rejectModal.releaseTitle}
        onConfirm={handleRejectConfirm}
        onCancel={handleRejectCancel}
      />
    </div>
  );
};

export default AllReleases; 